<script lang="ts">
import { ChevronLeftIcon } from '@lucide/vue';
import Toggle from '@/components/v2/Toggle.vue';
import UIButton from '@/components/ui/UIButton.vue';

export default {
  name: 'StockDropPage',
  components: {
    ChevronLeftIcon,
  },
};
</script>

<script setup lang="ts">
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import { computed, onMounted, ref } from 'vue';
import { compactNumber, toCurrency, toPercentage } from '@/utilities/Number';
import { colors } from '@/utilities/Color';
import { StockDropSettlement, StockDropModel, StockDropFactor } from '@/types/StockDropModel';
import { stockDropModelService, referenceService } from '@/services/api';
import { useRoute, useRouter } from 'vue-router';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import ExportMenu from '@/components/ExportMenu.vue';
import { inject } from 'vue';
import { exportToPDF, exportToPNG, getChartImages, getHeaderImage, getPNG } from '@/utilities/ExportCharts';
import { useStore } from 'vuex';
import { compactMoney } from '@/utilities/Money';
import { orgName } from '@/utilities/OrgConfig';
import { watch } from 'vue';
import UISelect from '@/components/ui/UISelect.vue';

// ------
// emits
// ------

const emit = defineEmits(['updated']);

const $route = useRoute();
const $router = useRouter();
const store = useStore();

// ----------
// refs
// ----------

const stockDropModel = ref<StockDropModel | null | undefined>(null);
const loading = ref<boolean>(true);
const selectedCategoryType = ref<string | null | undefined>(inject('selectedCategoryType'));
const exportMenuOptions = ref<string[]>(['Company Stock Data', 'Program Data', 'Hypothetical Drops', 'Chart']);
const isExporting = ref<boolean>(false);
const selectedSettlementPercent = ref<string>('50');
const selectedIndustry = ref<string>();
const isIPOFactorEnabled = ref<boolean>(false);
const settlementRanges = ref<StockDropSettlement[]>([]);
const selectedSettlementRanges = ref<StockDropSettlement[]>([]);
const stockDropFactors = ref<StockDropFactor[]>([]);

// ---------
// computed
// ---------
const totalCaseCount = computed(() => {
  return stockDropFactors.value.find((f) => f.type === 'Total')?.caseCount || 0;
});

const caseCountText = computed(() => {
  if (selectedIndustry.value === undefined && !isIPOFactorEnabled.value) {
    return totalCaseCount.value;
  } else {
    return `${caseCount.value} / ${totalCaseCount.value}`;
  }
});

const industryOptions = computed(() => {
  const industries = stockDropFactors.value.filter((factor) => factor.type === 'Industry');
  const uniqueIndustries = Array.from(new Set(industries)).sort();
  return uniqueIndustries.map((ind) => ({ label: ind.name, value: ind.id.toString() }));
});

const inputs = computed(() => ({
  companyName: stockDropModel.value?.accountName,
  asOfDate: stockDropModel.value?.asOfDate ? new Date(stockDropModel.value?.asOfDate).toLocaleDateString() : '',
  twoYearHighAmount: Number(stockDropModel.value?.twoYearHigh),
  shareOutstanding: Number(stockDropModel.value?.shareOutstanding),
  insiderOwnershipPercentage: Number(stockDropModel.value?.insiderOwnership) / 100,
  abcLimitsAmount: Number(stockDropModel.value?.abcLimits),
  sideALimitsAmount: Number(stockDropModel.value?.sideALimits),
  retentionAmount: Number(stockDropModel.value?.retention),
  defenseCostsPercentage: Number(stockDropModel.value?.defenseCosts) / 100,
  scenarioOnePercentage: Number(stockDropModel.value?.scenarioOne) / 100,
  scenarioTwoPercentage: Number(stockDropModel.value?.scenarioTwo) / 100,
  scenarioThreePercentage: Number(stockDropModel.value?.scenarioThree) / 100,
  scenarioFourPercentage: Number(stockDropModel.value?.scenarioFour) / 100,
  sideA: stockDropModel.value?.sideA,
  updatedDate: stockDropModel.value?.updatedDate
    ? new Date(stockDropModel.value?.updatedDate).toLocaleDateString()
    : '',
}));

const currentMarketCapAmount = computed(() => {
  if (stockDropModel.value?.twoYearHigh && stockDropModel.value?.shareOutstanding) {
    return stockDropModel.value.twoYearHigh * stockDropModel.value.shareOutstanding;
  }
  return 0;
});

// Extract unique percentages from settlement data
const availablePercentages = computed(() => {
  return Array.from(new Set(settlementRanges.value.map((range) => range.settlementPercent))).sort(
    (a, b) => a - b,
  );
});

// Set default percentage (50 or lowest available)
const defaultPercentage = computed(() => {
  if (availablePercentages.value.includes(50)) {
    return 50;
  }
  return availablePercentages.value.length > 0 ? availablePercentages.value[0] : 50;
});

// Update currentSettlementRanges based on selected percentage
const updateCurrentSettlementRanges = () => {
  selectedSettlementRanges.value = settlementRanges.value.filter(
    (range) => range.settlementPercent === Number(selectedSettlementPercent.value),
  );
};

const currentPublicFloatAmount = computed(() => {
  if (inputs.value?.insiderOwnershipPercentage) {
    return currentMarketCapAmount.value * (1 - inputs.value.insiderOwnershipPercentage);
  }
  return 0;
});

const totalInsuranceLayerAmount = computed(() => {
  let amount = Number(stockDropModel.value?.abcLimits) + Number(stockDropModel.value?.retention);

  if (stockDropModel.value?.sideA) {
    amount += Number(stockDropModel.value?.sideALimits);
  }

  return amount;
});

const hasAnalyticsWritePermissions = computed(() => {
  const roles = store.state.user?.roles || [];
  const permissions = store.state.user?.permissions || [];
  return roles.includes('admin') || permissions.includes('analytics:write');
});

const editRoute = computed(() => {
  const categoryId = $route.params.categoryId as string | undefined;
  return $route.params.id
    ? `/account/${stockDropModel.value?.accountId}/analytics/category/${categoryId}/stockdrop/${stockDropModel.value?.id}/edit`
    : `/analytics/category/${categoryId}/stockdrop/${stockDropModel.value?.id}/edit`;
});

function resolveScenarioLabel(key: string) {
  switch (key) {
    case 'scenarioOnePercentage':
      return 'Scenario 1';
    case 'scenarioTwoPercentage':
      return 'Scenario 2';
    case 'scenarioThreePercentage':
      return 'Scenario 3';
    case 'scenarioFourPercentage':
      return 'Scenario 4';
    default:
      return '';
  }
}

const hypotheticalDropData = computed(() => {
  const data: {
    scenarioLabel: string;
    percentDrop: number;
    retention: number;
    expectedDamages: number;
    settlementAsPercentOfTieredDamages: number;
    projectedSettlement: number;
    projectedSettlementPlusDefenseCosts: number;
    lossExcessOfABCLimitsAndRetention: number;
    lossExcessOfTotalLimitsAndRetention: number;
    insuredLossAmount: number;
    remainingLimitAmount: number;
    excessLossAmount: number;
  }[] = [];

  const scenarioKeys = [
    'scenarioOnePercentage',
    'scenarioTwoPercentage',
    'scenarioThreePercentage',
    'scenarioFourPercentage',
  ];

  for (const key of scenarioKeys) {
    let expectedDamages = currentPublicFloatAmount.value * inputs.value[key];
    if (isIPOFactorEnabled.value) {
      expectedDamages *= stockDropFactors.value.find((f) => f.type === 'IPO')?.riskFactor || 1;
    }

    if (selectedIndustry.value) {
      const industryFactor =
        stockDropFactors.value.find((f) => f.id.toString() === selectedIndustry.value)?.riskFactor || 1;
      expectedDamages *= industryFactor;
    }

    const settlementAsPercentOfTieredDamages = getSettlementAsPercentOfTieredDamages(expectedDamages);
    const projectedSettlement = expectedDamages * settlementAsPercentOfTieredDamages;
    const projectedSettlementPlusDefenseCosts = projectedSettlement * (1 + inputs.value?.defenseCostsPercentage);
    let lossExcessOfABCLimitsAndRetention =
      projectedSettlementPlusDefenseCosts -
      (Number(stockDropModel.value?.abcLimits) + Number(stockDropModel.value?.retention));

    if (lossExcessOfABCLimitsAndRetention < 0) {
      lossExcessOfABCLimitsAndRetention = 0;
    }

    let lossExcessOfTotalLimitsAndRetention =
      projectedSettlementPlusDefenseCosts -
      (Number(stockDropModel.value?.abcLimits) +
        Number(stockDropModel.value?.sideALimits) +
        Number(stockDropModel.value?.retention));

    if (lossExcessOfTotalLimitsAndRetention < 0) {
      lossExcessOfTotalLimitsAndRetention = 0;
    }

    let insuredLossAmount = 0;
    if (projectedSettlementPlusDefenseCosts > totalInsuranceLayerAmount.value) {
      insuredLossAmount = totalInsuranceLayerAmount.value - inputs?.value.retentionAmount;
    } else {
      insuredLossAmount = Math.max(0, projectedSettlementPlusDefenseCosts - Number(stockDropModel.value?.retention));
    }

    const remainingLimitAmount = totalInsuranceLayerAmount.value - projectedSettlementPlusDefenseCosts;

    // Calculate actual retention
    const actualRetentionAmount = Math.min(
      projectedSettlementPlusDefenseCosts,
      Number(stockDropModel.value?.retention),
    );

    data.push({
      scenarioLabel: resolveScenarioLabel(key),
      percentDrop: inputs.value[key],
      retention: actualRetentionAmount,
      expectedDamages,
      settlementAsPercentOfTieredDamages: settlementAsPercentOfTieredDamages,
      projectedSettlement,
      projectedSettlementPlusDefenseCosts,
      lossExcessOfABCLimitsAndRetention: lossExcessOfABCLimitsAndRetention < 0 ? 0 : lossExcessOfABCLimitsAndRetention,
      lossExcessOfTotalLimitsAndRetention:
        lossExcessOfTotalLimitsAndRetention < 0 ? 0 : lossExcessOfTotalLimitsAndRetention,
      insuredLossAmount,
      remainingLimitAmount: remainingLimitAmount < 0 ? 0 : remainingLimitAmount,
      excessLossAmount: inputs.value?.sideA ? lossExcessOfTotalLimitsAndRetention : lossExcessOfABCLimitsAndRetention,
    });
  }

  return data.sort((a, b) => a.percentDrop - b.percentDrop);
});

// Chart data
const chartDataSource = computed(() => {
  return {
    chart: {
      numberPrefix: '$',
      theme: 'cacs',
      placeValuesInside: '0',
      valuePadding: '0',
      valueFontBold: '0',
      baseFontColor: '#000000',
      valueBgAlpha: '50',
      paletteColors: ['black', colors.specialty_teal, colors.light_gray, colors.gold],
    },
    categories: [
      {
        category: hypotheticalDropData.value.map((data) => {
          return { label: `${asPercentage(data.percentDrop)}` };
        }),
      },
    ],
    dataset: [
      {
        dataset: [
          {
            seriesname: 'Retention',
            data: hypotheticalDropData.value.map((d) => ({
              value: d.retention,
              valueFontColor: '#FFFFFFF',
              valueBgColor: '#000000',
              valueBgAlpha: '0',
              showValue: d.retention > 0 ? '1' : '0',
            })),
          },
          {
            seriesname: 'Insured Loss Amount',
            data: hypotheticalDropData.value.map((d) => ({
              value: d.insuredLossAmount,
              valueFontColor: '#FFFFFF',
              valueBgColor: '#000000',
              valueBorderRadius: '10',
              showValue: d.insuredLossAmount > 0 ? '1' : '0',
            })),
          },
          {
            seriesname: 'Remaining Limit',
            data: hypotheticalDropData.value.map((d) => ({
              value: d.remainingLimitAmount,
              showValue: d.remainingLimitAmount > 0 ? '1' : '0',
            })),
          },
          {
            seriesname: 'Excess Loss',
            data: hypotheticalDropData.value.map((d) => ({
              value: d.excessLossAmount,
              showValue: d.excessLossAmount > 0 ? '1' : '0',
            })),
          },
        ],
      },
    ],
  };
});

const caseCount = computed(() => {
  if (isIPOFactorEnabled.value) {
    return stockDropFactors.value.find((f) => f.type === 'IPO')?.caseCount || 0;
  }

  if (selectedIndustry.value) {
    return stockDropFactors.value.find((f) => f.id.toString() === selectedIndustry.value)?.caseCount || 0;
  }

  return 0;
});

// ---------
// Watchers
// ---------

// Watch for changes in settlement data to update selected percentage and current ranges
watch([settlementRanges, defaultPercentage], () => {
  if (settlementRanges.value.length > 0) {
    selectedSettlementPercent.value = defaultPercentage.value.toString();
    updateCurrentSettlementRanges();
  }
});

// Watch for changes in selected percentage to update current ranges
watch(selectedSettlementPercent, () => {
  updateCurrentSettlementRanges();
});

watch(selectedIndustry, (i) => {
  if (i) {
    isIPOFactorEnabled.value = false;
  }
});

watch(isIPOFactorEnabled, (enabled) => {
  if (enabled) {
    selectedIndustry.value = undefined;
  }
});

// -----------
// lifecycles
// -----------

onMounted(async () => {
  stockDropFactors.value = await referenceService.stockDropFactors();
  await refresh();
});

// ----------
// functions
// ----------

async function refresh() {
  loading.value = true;
  settlementRanges.value = await stockDropModelService.listSettlementRanges();
  stockDropModel.value = await stockDropModelService.findById($route.params.stockdropId as string);
  loading.value = false;
}

function onBackButtonClick() {
  // Emit updated event to refresh the parent list view
  emit('updated');
  
  if (selectedCategoryType.value) {
    const route = $route.params.id
      ? `/account/${stockDropModel.value?.accountId}/analytics/category/${selectedCategoryType.value}`
      : `/analytics/category/${selectedCategoryType.value}`;
    return $router.push(route);
  } else {
    const route = $route.params.id
      ? `/account/${stockDropModel.value?.accountId}/analytics`
      : '/analytics';
    return $router.push(route);
  }
}

function asCurrency(value: number | undefined, opts?: Intl.NumberFormatOptions) {
  if (value) {
    return toCurrency(value, { minimumFractionDigits: 2, maximumFractionDigits: 2, ...opts });
  } else {
    return '--';
  }
}

function asCompactCurrency(value: number | undefined) {
  if (value) {
    return compactMoney(value);
  } else {
    return '--';
  }
}

function asCompactNumber(value: number | undefined) {
  if (value) {
    return compactNumber(value);
  } else {
    return '--';
  }
}

function asPercentage(value: number) {
  if (value) {
    return toPercentage(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    return '--';
  }
}

function getSettlementAsPercentOfTieredDamages(expectedDamagesAmount: number) {
  for (const settlementRange of selectedSettlementRanges.value) {
    const { minDamages, maxDamages, settlement } = settlementRange;
    if (expectedDamagesAmount >= minDamages && (maxDamages === null || expectedDamagesAmount < maxDamages)) {
      return settlement / 100;
    }
  }
  return 0;
}

async function handleExport(type: string, selected: string[], finishExport: () => void, oneFile?: boolean) {
  isExporting.value = true;
  // gather element IDs for each chart
  const elementIds = selected
    .map((c) => {
      switch (c) {
        case 'Company Stock Data':
          return 'company-stock-data';
        case 'Program Data':
          return 'program-data';
        case 'Hypothetical Drops':
          return 'hypothetical-drops-table';
        case 'Chart':
          return 'stock-drop-chart';
        default:
          return null;
      }
    })
    .filter((id) => id !== null) as string[];

  const currentDate = new Date();
  const formattedDate = currentDate.toISOString().split('T')[0].replace(/-/g, '.');
  const org = orgName();
  const filename = `${org ? org + '-' : ''}${stockDropModel.value?.name}_${formattedDate}`;

  if (oneFile) {
    const images = await getChartImages(elementIds);
    const maxWidth = Math.max(...images.map(({ width }) => width));
    const header = await getHeaderImage(maxWidth, stockDropModel.value?.name ?? '');
    const png = await getPNG(images, header);

    if (type === 'png') {
      await exportToPNG(png, filename + '.png');
    } else if (type === 'pdf') {
      await exportToPDF(images, header, filename + '.pdf');
    } else {
      console.log(`unexpected value ${type} for export type`);
    }
  } else {
    // Export each item individually
    for (const [_, elementId] of elementIds.entries()) {
      const images = await getChartImages([elementId]);
      const maxWidth = Math.max(...images.map(({ width }) => width));
      const header = await getHeaderImage(maxWidth, stockDropModel.value?.name ?? '');
      const png = await getPNG(images, header);

      if (type === 'png') {
        await exportToPNG(png, `${filename}-${elementId}.png`);
      } else if (type === 'pdf') {
        await exportToPDF(images, header, `${filename}-${elementId}.pdf`);
      } else {
        console.log(`unexpected value ${type} for export type`);
      }
    }
  }

  isExporting.value = false;
  finishExport();
}
</script>

<template>
  <PageSpinner class="loader" v-if="loading" />
  <div class="m-4 flex flex-col gap-2" v-else>
    <div class="flex items-center justify-between">
      <div class="flex flex-col gap-2">
        <h1>Stock Drop Model</h1>
        <div class="flex items-end gap-2">
          <h2>{{ stockDropModel?.name }}</h2>
          <span class="text-md text-primary italic">{{ stockDropModel?.accountName }}</span>
        </div>
      </div>
    </div>

    <div class="divider m-0 mx-auto w-full"></div>
    <div class="flex items-center gap-2">
      <div class="btn flex items-center justify-items-start gap-1 btn-sm" @click="onBackButtonClick">
        <ChevronLeftIcon :size="15" />
        <span class="mr-1">Back</span>
      </div>

      <router-link v-if="hasAnalyticsWritePermissions" class="btn btn-sm" :to="editRoute">
        <span class="">Edit Model</span>
      </router-link>
      <ExportMenu :items="exportMenuOptions" support-individual-exports @export="handleExport" />
    </div>

    <div class="mx-auto flex flex-col items-center gap-2 py-6">
      <div id="company-stock-data" class="mx-auto flex gap-6 px-8">
        <div class="text-center">
          <div class="text-[0.8em] uppercase">Stock Price</div>
          <div class="text-lg font-bold">{{ asCurrency(inputs.twoYearHighAmount) }}</div>
        </div>
        <div class="text-center">
          <div class="text-[0.8em] uppercase">Shares Outstanding</div>
          <div class="text-lg font-bold">{{ asCompactNumber(stockDropModel?.shareOutstanding) }}</div>
        </div>
        <div class="text-center">
          <div class="text-[0.8em] uppercase">Insider Ownership</div>
          <div class="text-lg font-bold">{{ asPercentage(inputs?.insiderOwnershipPercentage) }}</div>
        </div>
        <div class="text-center">
          <div class="text-[0.8em] uppercase">Market Cap</div>
          <div class="text-lg font-bold">{{ asCompactCurrency(currentMarketCapAmount) }}</div>
        </div>
        <div class="text-center">
          <div class="text-[0.8em] uppercase">Public Float</div>
          <div class="text-lg font-bold">{{ asCompactCurrency(currentPublicFloatAmount) }}</div>
        </div>
        <div class="text-center transition-all">
          <div class="text-[0.8em] uppercase">Case Count</div>
          <div class="text-lg font-bold">{{ caseCountText }}</div>
        </div>
      </div>
      <div
        class="tooltip tooltip-bottom text-xs italic"
        :data-tip="`Data updated on ${inputs?.updatedDate}`">
        2-YEAR HIGHS ({{ inputs.asOfDate }})*
      </div>
    </div>

    <!-- Overlay to prevent hover tooltips/interaction while exporting -->
    <div v-if="isExporting" class="absolute z-20 h-3/4 w-full opacity-0"></div>

    <!-- Settlement Percentile and Factors -->
    <div
      class="mx-auto mb-4 flex max-w-5xl items-center justify-center gap-6 rounded-md border-2 border-base-300 p-4 shadow shadow-base-200/20">
      <div class="flex items-center gap-6">
        <Toggle variant="secondary" label="IPO Factor" v-model="isIPOFactorEnabled" />
        <div class="flex items-center gap-1">
          <span class="label">Industry Factor:</span>
          <div class="flex items-center gap-2">
            <UISelect v-model="selectedIndustry" :options="industryOptions" placeholder="Select an Industry" />
            <UIButton
              v-if="selectedIndustry !== undefined"
              variant="outline"
              size="sm"
              class="border-red-200 hover:border-secondary/0"
              @click="selectedIndustry = undefined">
              Clear
            </UIButton>
          </div>
        </div>
        <div class="flex items-center gap-1">
          <span class="label">Settlement Percentile:</span>
          <UISelect
            v-model="selectedSettlementPercent"
            :options="availablePercentages.map((p) => ({ value: p.toString(), label: `${p}th` }))"
            class="h-9 w-[120px]"
            placeholder="Select %" />
        </div>
      </div>
    </div>

    <div class="flex flex-col items-center overflow-auto pt-2">
      <div id="stock-drop-chart" class="w-11/12 max-w-5xl">
        <EmptyDisplay bordered height="500px" title="Stock Drop Chart" text="No limit data available." v-if="false" />
        <fusioncharts
          v-else
          type="msstackedcolumn2d"
          width="100%"
          height="500"
          dateFormat="json"
          :dataSource="chartDataSource" />
      </div>

      <div class="divider mx-auto mt-0 w-3/4"></div>

      <h4 class="font-normal">Program Data</h4>
      <div class="mx-auto flex">
        <section id="program-data" class="mx-auto flex justify-center gap-8 px-8 pt-2 pb-4">
          <div class="text-center">
            <div class="w-48 text-[0.8em] uppercase"></div>
          </div>
          <div class="text-center">
            <div class="text-[0.8em] uppercase">ABC Limits</div>
            <div class="text-lg font-bold">{{ asCompactCurrency(inputs.abcLimitsAmount) }}</div>
          </div>
          <div class="text-center">
            <div class="text-[0.8em] uppercase">Side A Limits</div>
            <div class="text-lg font-bold">{{ asCompactCurrency(inputs.sideALimitsAmount) }}</div>
          </div>
          <div class="text-center">
            <div class="text-[0.8em] uppercase">Retention</div>
            <div class="text-lg font-bold">{{ asCompactCurrency(inputs.retentionAmount) }}</div>
          </div>
          <div class="text-center">
            <div class="text-[0.8em] uppercase">Defense Costs</div>
            <div class="text-lg font-bold">{{ asPercentage(inputs.defenseCostsPercentage) }}</div>
          </div>
          <div class="text-center">
            <div class="w-40 text-[0.8em] uppercase"></div>
          </div>
        </section>
      </div>

      <div id="hypothetical-drops" class="mt-4">
        <table id="hypothetical-drops-table" class="table border-collapse table-sm font-bold">
          <thead class="text-center">
            <tr class="text-slate-800">
              <th class=""></th>
              <th v-for="(data, index) in hypotheticalDropData" :key="index" class="text-base">
                {{ data.scenarioLabel }}
              </th>
            </tr>
            <tr class="h-10 bg-gray-100 text-base text-primary">
              <th class="text-left">Hypothetical Drop</th>
              <th v-for="(data, index) in hypotheticalDropData" :key="index">{{ asPercentage(data.percentDrop) }}</th>
            </tr>
          </thead>
          <tbody class="text-center">
            <tr class="border-none">
              <td class="text-left">Expected Damages</td>

              <td v-for="(data, index) in hypotheticalDropData" :key="index">
                {{ asCurrency(data.expectedDamages, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) }}
              </td>
            </tr>
            <tr class="border-none">
              <td class="text-left">Settlement As % of Tiered Damages</td>
              <td v-for="(data, index) in hypotheticalDropData" :key="index">
                {{ asPercentage(data.settlementAsPercentOfTieredDamages) }}
              </td>
            </tr>
            <tr class="border-none">
              <td class="text-left">Projected Settlement Amount</td>
              <td v-for="(data, index) in hypotheticalDropData" :key="index">
                {{ asCurrency(data.projectedSettlement, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) }}
              </td>
            </tr>
            <tr class="border-none">
              <td class="text-left">
                Projected Settlement Amount + Defense Costs {{ inputs.defenseCostsPercentage * 100 }}%
              </td>
              <td v-for="(data, index) in hypotheticalDropData" :key="index">
                {{
                  asCurrency(data.projectedSettlementPlusDefenseCosts, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                }}
              </td>
            </tr>
            <tr class="border-none">
              <td class="text-left">Loss Excess of ABC Limits & Retention</td>
              <td v-for="(data, index) in hypotheticalDropData" :key="index">
                {{
                  asCurrency(data.lossExcessOfABCLimitsAndRetention, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                }}
              </td>
            </tr>
            <tr class="border-none">
              <td class="text-left">Loss Excess of Total Limits & Retention</td>
              <td v-for="(data, index) in hypotheticalDropData" :key="index">
                {{
                  asCurrency(data.lossExcessOfTotalLimitsAndRetention, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                }}
              </td>
            </tr>
          </tbody>
        </table>
        <div class="flex gap-6 pt-4"></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
